import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { createModernTheme } from './theme/modernTheme';
import ModernBackground from './components/common/ModernBackground';
import { BrandingProvider } from './components/common/BrandingSystem';
import { LanguageProvider } from './contexts/LanguageContext';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import ModularDashboard from './components/ModularDashboard';
import DashboardOverview from './components/DashboardOverview';
import UserProfile from './components/UserProfile';
import RoomControl from './components/RoomControl';
import SensorStatusPage from './components/SensorStatusPage';
import Harvested from './components/Harvested';
import ProjectSettings from './components/ProjectSettings';
import BrandingSettings from './components/BrandingSettings';
import AdminDashboard from './components/AdminDashboard';
import ProtectedRoute from './components/ProtectedRoute';
import AutomationPage from './components/AutomationControlPage';
import AdminRoute from './components/AdminRoute';
import useIsMobile from './components/hooks/useIsMobile';
import { LocalNotifications } from '@capacitor/local-notifications';

// 📌 Importar los nuevos componentes
import Employments from './components/Facility/Employments';
import Visitors from './components/Facility/Visitors';
import AccessLogs from './components/Facility/AccessLogs';
import AccessValidation from './components/Facility/AccessValidation';
import PublicAccessValidation from './components/Facility/PublicAccessValidation';
import EmployeeAccess from './components/Facility/EmployeeAccess';
import EmployeeAccessAI from './components/Facility/EmployeeAccessAI';
import EmployeeDashboard from './components/Facility/EmployeeDashboard';

// 🆕 UniFi Control Components
import UniFiControl from './components/UniFiControl/UniFiControl';
import UniFiManagement from './components/UniFiControl/UniFiManagement';
import UniFiDiagnostics from './components/UniFiControl/UniFiDiagnostics';
import UniFiSetupWizard from './components/UniFiControl/UniFiSetupWizard';

// 🆕 Room Display Components
import RoomLandingPage from './components/RoomLandingPage';
import RoomDisplayManager from './components/RoomDisplayManager';

// 🆕 Sensor Adoption Component
import SensorAdoption from './components/SensorAdoption';

// 🆕 Task Management Components
import TaskDashboard from './components/TaskManagement/TaskDashboard';
import BudgetOverview from './components/TaskManagement/BudgetOverview';
import ProjectEstimator from './components/TaskManagement/ProjectEstimator';
import TeamWorkload from './components/TaskManagement/TeamWorkload';
import DatabaseUpgradeWizard from './components/DatabaseUpgrade/DatabaseUpgradeWizard';

// 🆕 AI Assistant and Tour Components
import AIAssistant from './components/AIAssistant';
import AdminTour from './components/AdminTour';

// 🆕 Database Manager Component
import DatabaseManager from './components/DatabaseManager';

// 🆕 Time Tracker Components
import ConditionalTimeTracker from './components/ConditionalTimeTracker';
import TimeTrackerDashboard from './components/TimeTracker/TimeTrackerDashboard';

// 🆕 Schedule/Calendar Components
import ScheduleCalendar from './components/Schedule/ScheduleCalendar';
import EmployeeMap from './components/Schedule/EmployeeMap';
import WhoIsWorking from './components/Schedule/WhoIsWorking';

// 🆕 Task Kanban Component
import TaskKanban from './components/TaskManagement/TaskKanban';

// 🆕 Sensor Analytics Component
import SensorAnalytics from './components/SensorAnalytics';

function App() {
  const isMobile = useIsMobile();
  const [themeMode, setThemeMode] = useState('light');

  // Create theme instance
  const theme = createModernTheme(themeMode);

  useEffect(() => {
    if (isMobile) {
      // Solicita permisos para notificaciones en móviles
      LocalNotifications.requestPermissions().then(result => {
        if (result.display === 'granted') {
          console.log("Permiso para notificaciones locales concedido");
        }
      }).catch(error => console.error("Error solicitando permisos para notificaciones locales:", error));
    }
  }, [isMobile]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LanguageProvider>
        <BrandingProvider>
          <ModernBackground>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/register/:projectId" element={<Register />} />

              {/* Protege la ruta del dashboard - Ahora apunta al DashboardOverview */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <DashboardOverview />
                  </ProtectedRoute>
                } 
              />

              {/* Nueva ruta para el dashboard modular */}
              <Route 
                path="/modular-dashboard" 
                element={
                  <ProtectedRoute>
                    <ModularDashboard />
                  </ProtectedRoute>
                } 
              />

              {/* Ruta específica para el dashboard overview moderno */}
              <Route 
                path="/dashboard-overview" 
                element={
                  <ProtectedRoute>
                    <DashboardOverview />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <UserProfile />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/harvested" 
                element={
                  <ProtectedRoute>
                    <Harvested />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/room-control" 
                element={
                  <ProtectedRoute>
                    <RoomControl />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/project-settings" 
                element={
                  <ProtectedRoute>
                    <ProjectSettings />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/branding-settings" 
                element={
                  <ProtectedRoute>
                    <BrandingSettings />
                  </ProtectedRoute>
                } 
              />

              <Route
                path="/sensor-status"
                element={
                  <ProtectedRoute>
                    <SensorStatusPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/automation-status"
                element={
                  <ProtectedRoute>
                    <AutomationPage />
                  </ProtectedRoute>
                }
              />

              {/* Rutas protegidas de administración */}
              <Route 
                path="/admin" 
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                } 
              />

              {/* 🆕 Database Manager - Solo para administradores */}
              <Route 
                path="/admin/database-manager" 
                element={
                  <AdminRoute>
                    <DatabaseManager />
                  </AdminRoute>
                } 
              />

              {/* 📌 Facility Management - Protegidas para administradores */}
              <Route 
                path="/facility/employments" 
                element={
                  <AdminRoute>
                    <Employments />
                  </AdminRoute>
                } 
              />

              <Route 
                path="/facility/visitors" 
                element={
                  <AdminRoute>
                    <Visitors />
                  </AdminRoute>
                } 
              />

              <Route 
                path="/facility/access-logs" 
                element={
                  <AdminRoute>
                    <AccessLogs />
                  </AdminRoute>
                } 
              />

              {/* 📌 Ruta pública para Validación de Acceso (para seguridad en la entrada) */}
              <Route 
                path="/access-validation" 
                element={<AccessValidation />}
              />
              <Route 
                path="/access-worker" 
                element={
                    <EmployeeAccess />
                } 
              />
              <Route 
                path="/access-worker-ai" 
                element={
                    <EmployeeAccessAI />
                } 
              />

              {/* 📌 Ruta pública para Dashboard de Empleados (para ver su tabla de trabajo) */}
              <Route 
                path="/my-work-table" 
                element={<EmployeeDashboard />}
              />

              {/* 🆕 UniFi Control Routes */}
              <Route 
                path="/unifi-control" 
                element={
                  <AdminRoute>
                    <UniFiControl />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/unifi-management" 
                element={
                  <AdminRoute>
                    <UniFiManagement />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/unifi-diagnostics" 
                element={
                  <AdminRoute>
                    <UniFiDiagnostics />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/unifi-setup-wizard" 
                element={
                  <AdminRoute>
                    <UniFiSetupWizard />
                  </AdminRoute>
                } 
              />

              {/* 🆕 Room Display Routes */}
              <Route 
                path="/room-display/:roomId" 
                element={<RoomLandingPage />}
              />
              <Route 
                path="/admin/room-displays" 
                element={
                  <AdminRoute>
                    <RoomDisplayManager />
                  </AdminRoute>
                } 
              />

              {/* 🆕 Sensor Adoption Component */}
              <Route 
                path="/sensor-adoption" 
                element={
                  <ProtectedRoute>
                    <SensorAdoption />
                  </ProtectedRoute>
                } 
              />

              {/* 🆕 Sensor Analytics Component */}
              <Route 
                path="/sensor-analytics" 
                element={
                  <ProtectedRoute>
                    <SensorAnalytics />
                  </ProtectedRoute>
                } 
              />

              {/* 🆕 Task Management Routes */}
              <Route 
                path="/task-dashboard" 
                element={
                  <ProtectedRoute>
                    <TaskDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/budget-overview" 
                element={
                  <ProtectedRoute>
                    <BudgetOverview />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/project-estimator" 
                element={
                  <ProtectedRoute>
                    <ProjectEstimator />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/team-workload" 
                element={
                  <ProtectedRoute>
                    <TeamWorkload />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/database-upgrade-wizard" 
                element={
                  <ProtectedRoute>
                    <DatabaseUpgradeWizard />
                  </ProtectedRoute>
                } 
              />

              {/* 🆕 Task Kanban Route */}
              <Route 
                path="/task-kanban" 
                element={
                  <ProtectedRoute>
                    <TaskKanban />
                  </ProtectedRoute>
                } 
              />

              {/* 🆕 Time Tracker Routes */}
              <Route 
                path="/time-tracker" 
                element={
                  <ProtectedRoute>
                    <TimeTrackerDashboard />
                  </ProtectedRoute>
                } 
              />

              {/* 🆕 Schedule Routes */}
              <Route 
                path="/schedule/calendar" 
                element={
                  <AdminRoute>
                    <ScheduleCalendar />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/schedule/employee-map" 
                element={
                  <AdminRoute>
                    <EmployeeMap />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/schedule/who-is-working" 
                element={
                  <ProtectedRoute>
                    <WhoIsWorking />
                  </ProtectedRoute>
                } 
              />

              {/* 🆕 AI Assistant and Tour Routes */}
              <Route 
                path="/ai-assistant" 
                element={
                  <ProtectedRoute>
                    <AIAssistant />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin-tour" 
                element={
                  <ProtectedRoute>
                    <AdminTour />
                  </ProtectedRoute>
                } 
              />
            </Routes>
            
            {/* Time Tracker Widget - Solo visible cuando estás autenticado */}
            <ConditionalTimeTracker />
          </ModernBackground>
        </BrandingProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;

